import {
  forwardRef,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type ReactNode,
  type Ref,
} from 'react';
import './Button.css';

/**
 * 공통 Button 컴포넌트 — 본 cycle (애니메이션 추가 옵션 B) 첫 도입.
 *
 * variants:
 *   - primary  : --primary 배경 + --primary-text 글씨
 *   - secondary: --surface 배경 + --border 테두리
 *   - ghost    : 배경 투명 (hover 시 --surface-2)
 *   - danger   : --color-danger 배경
 *   - soft     : --primary-tint 배경 + --primary-ink 글씨 (정본 D1 · 2026-09-02)
 *   - quiet-danger : 평소엔 테두리만, hover 때 붉어진다 (정본 D1 · 2026-09-02 신설)
 *
 * sizes:
 *   - sm     : 24px height (--h-xs)
 *   - compact: 28px height (--h-sm) — 실측 1위 높이. 2026-09-02 신설
 *   - md     : 32px height (--h-md · 기본)
 *   - lg     : 40px height (--h-lg)
 *
 * ── 2026-09-02 · 채택 3단계 — **빠진 넷을 채운다** (정본 axi-components.html D1) ──────
 * 이 컴포넌트는 만들어져 있는데도 **프로덕션 사용이 0건**이었다. 정본이 그 이유를 넷으로
 * 못 박아 두었다 — 화면들이 «안 쓴» 것이 아니라 **쓸 수 없었다**:
 *
 *   ① `iconOnly` 가 없다      → 정사각 아이콘 단추가 안 나온다 (화면마다 손으로 만든다)
 *   ② `fullWidth` 가 없다     → 모달 하단 단추가 못 늘어난다
 *   ③ **28px 단이 없다**      → 실측 1위가 28인데 사다리가 24/32/40 이라 2~4px 씩 어긋난다
 *   ④ `href` 가 없다          → 링크로 생긴 단추를 못 만든다
 *
 * ★ **`sm` 을 24 → 28 로 바꾸지 않는다**(정본 경고). 이미 `sm` 을 쓰는 자리가 소리 없이
 *   커진다 — 사다리에 `compact`(28)를 **끼워 넣는다**.
 * ★ `iconOnly` 는 `aria-label` **필수**다. `title=` 로 대체하지 않는다(읽어 주지 않는다).
 *
 * ── 2026-09-02 · `quiet-danger` — 목록 줄 끝의 삭제 (정본 D1) ──────────────────
 * ★★ **줄마다 solid 빨강을 세울 수 없다.** 스무 줄짜리 목록이면 빨간 덩어리가 스무 개다.
 *    그렇다고 다른 작업과 똑같이 두면 **되돌릴 수 없는 일이 담담한 일과 구별되지 않는다.**
 *    → 평소엔 `secondary` 처럼 테두리만, **손이 닿을 때 붉어진다.**
 * ★★ **링크형(글자만 있는 단추)은 만들지 않는다**(2026-09-02 사용자 결정).
 *    목록 안에서 무언가 하는 것은 전부 테두리가 있는 단추다 — 파란 글씨는 진짜 링크와
 *    구별이 사라지고, `ghost` 는 회색이라 「눌리는 것」으로 안 읽힌다.
 * ★ solid `danger` 는 **확인 창 안의 마지막 단추**로 쓴다. 화면에 상시로 세우지 않는다.
 *
 * loading:
 *   - aria-busy=true + disabled + 내부 spinner. children 은 보이게 유지.
 *
 * pressed feedback:
 *   - :active 시 scale(0.97) + --duration-instant.
 *
 * accessibility:
 *   - disabled 시 aria-disabled / pointer-events:none 자동.
 *   - keyboard 'Enter'/'Space' 는 native button 자동.
 */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'soft' | 'quiet-danger';
export type ButtonSize = 'sm' | 'compact' | 'md' | 'lg';

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'disabled'>,
    Pick<AnchorHTMLAttributes<HTMLAnchorElement>, 'target' | 'rel' | 'download'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  /** loading 또는 disabled 둘 중 하나라도 true 면 비활성. */
  disabled?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  children?: ReactNode;
  /**
   * 아이콘만 있는 정사각 단추. 높이 = 폭이 되고 좌우 여백이 0 이 된다.
   * ★ `aria-label` 이 **필수**다 — 없으면 스크린리더가 「단추」로만 읽는다.
   */
  iconOnly?: boolean;
  /** 칸을 가득 채운다 — 모달 하단·좁은 폼. */
  fullWidth?: boolean;
  /**
   * 있으면 `<a>` 로 그린다 — 「링크인데 단추로 보여야 하는 것」.
   * 비활성일 때는 `href` 를 떼고 `aria-disabled` 를 단다(눌리지 않게).
   */
  href?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    disabled = false,
    iconLeft,
    iconRight,
    iconOnly = false,
    fullWidth = false,
    href,
    target,
    rel,
    download,
    children,
    className,
    type = 'button',
    ...rest
  },
  ref,
) {
  const isInactive = loading || disabled;
  const cn = [
    'wc-btn',
    `wc-btn--${variant}`,
    `wc-btn--${size}`,
    iconOnly ? 'wc-btn--icon' : '',
    fullWidth ? 'wc-btn--full' : '',
    loading ? 'wc-btn--loading' : '',
    isInactive ? 'wc-btn--inactive' : '',
    className ?? '',
  ].filter(Boolean).join(' ');

  if (import.meta.env.DEV && iconOnly && !rest['aria-label'] && !rest['aria-labelledby']) {
    // 아이콘만 있는 단추는 이름이 없으면 「단추」로만 읽힌다. title= 은 읽어 주지 않는다.
    console.warn('[Button] iconOnly 인데 aria-label 이 없다.');
  }

  const inner = (
    <>
      {loading && <span className="wc-btn__spinner" aria-hidden />}
      {iconLeft && <span className="wc-btn__icon wc-btn__icon--left" aria-hidden>{iconLeft}</span>}
      {children !== undefined && <span className="wc-btn__label">{children}</span>}
      {iconRight && <span className="wc-btn__icon wc-btn__icon--right" aria-hidden>{iconRight}</span>}
    </>
  );

  /* 링크로 생긴 단추 — 비활성이면 href 를 떼야 «눌러지지» 않는다(aria-disabled 만으로는 눌린다). */
  if (href !== undefined) {
    return (
      <a
        ref={ref as unknown as Ref<HTMLAnchorElement>}
        href={isInactive ? undefined : href}
        target={target}
        rel={rel}
        download={download}
        className={cn}
        aria-busy={loading || undefined}
        aria-disabled={isInactive || undefined}
        {...(rest as AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {inner}
      </a>
    );
  }

  return (
    <button
      ref={ref}
      type={type}
      className={cn}
      disabled={isInactive}
      aria-busy={loading || undefined}
      aria-disabled={isInactive || undefined}
      {...rest}
    >
      {inner}
    </button>
  );
});
